import { TUser } from 'api/users'
import * as t from 'io-ts'
import { nullable } from 'lib/request'

import { TMembertatus } from '../projects.codecs'

export const TChanged = t.partial({
  key: t.string,
  lang: t.string,
  oldValue: t.union([t.string, t.array(t.string)]),
  newValue: t.union([t.string, t.array(t.string)]),
  oldRepoName: t.string,
  newRepoName: t.string,
})

export const TProjectCreatedAction = t.strict({
  action: t.literal('project_created'),
  changed: t.null,
})

export const TProjectRemovedAction = t.strict({
  action: t.literal('project_removed'),
  changed: t.null,
})

export const TProjectAvatarUpdatedAction = t.strict({
  action: t.literal('project_avatar_updated'),
  changed: t.strict({
    oldValue: nullable(t.string),
    newValue: nullable(t.string),
  }),
})

export const TProjectNameUpdatedAction = t.strict({
  action: t.literal('project_name_updated'),
  changed: t.strict({
    oldValue: nullable(t.string),
    newValue: nullable(t.string),
  }),
})

export const TProjectDescriptionUpdatedAction = t.strict({
  action: t.literal('project_desc_updated'),
  changed: t.strict({
    oldValue: nullable(t.string),
    newValue: nullable(t.string),
  }),
})

export const TProjectMainLangUpdatedAction = t.strict({
  action: t.literal('project_main_lang_updated'),
  changed: t.strict({
    newValue: t.string,
    oldValue: t.string,
  }),
})

export const TProjectLanguagesUpdatedAction = t.strict({
  action: t.literal('project_langs_updated'),
  changed: t.strict({
    oldValue: t.array(t.string),
    newValue: t.array(t.string),
  }),
})

export const TCollaboratorAddedAction = t.strict({
  action: t.literal('collaborator_added'),
  changed: t.strict({
    newValue: t.string,
  }),
})

export const TCollabortorRemovedAction = t.strict({
  action: t.literal('collaborator_removed'),
  changed: t.strict({
    oldValue: t.string,
  }),
})

export const TCollaboratorStatusChangedAction = t.strict({
  action: t.literal('collaborator_status_changed'),
  changed: t.strict({
    userId: t.string,
    memberFullName: t.string,
    newValue: TMembertatus,
    oldValue: TMembertatus,
  }),
})

export const TTermCreatedAction = t.strict({
  action: t.literal('term_created'),
  changed: t.strict({
    key: t.string,
  }),
})

export const TTermDeletedAction = t.strict({
  action: t.literal('term_deleted'),
  changed: t.strict({
    key: t.string,
  }),
})

export const TTermKeyUpdatedAction = t.strict({
  action: t.literal('term_key_updated'),
  changed: t.strict({
    oldValue: t.string,
    newValue: t.string,
  }),
})

export const TTermDescriptionUpdated = t.strict({
  action: t.literal('term_desc_updated'),
  changed: t.strict({
    key: t.string,
    oldValue: nullable(t.string),
    newValue: nullable(t.string),
  }),
})

export const TTermTagsUpdatedAction = t.strict({
  action: t.literal('term_tags_updated'),
  changed: t.strict({
    key: t.string,
    oldValue: t.array(t.string),
    newValue: t.array(t.string),
  }),
})

export const TTranslationAddedAction = t.strict({
  action: t.literal('translation_added'),
  changed: t.strict({
    key: t.string,
    lang: t.string,
    newValue: t.string,
  }),
})

export const TTranslationUpdatedAction = t.strict({
  action: t.literal('translation_updated'),
  changed: t.strict({
    key: t.string,
    lang: t.string,
    oldValue: t.string,
    newValue: t.string,
  }),
})

export const TTranslationDeletedAction = t.strict({
  action: t.literal('translation_removed'),
  changed: t.strict({
    key: t.string,
    lang: t.string,
    oldValue: t.string,
  }),
})

export const TProjectLinkedAction = t.strict({
  action: t.literal('project_linked'),
  changed: t.strict({
    oldValue: nullable(t.string),
    newValue: nullable(t.string),
  }),
})

export const TGhRepoAddedAction = t.strict({
  action: t.literal('gh_repo_added'),
  changed: t.strict({
    newRepoName: t.string,
  }),
})

export const TGhRepoDeletedAction = t.strict({
  action: t.literal('gh_repo_removed'),
  changed: t.strict({
    oldRepoName: t.string,
  }),
})

export const TGitLabRepoAddedAction = t.strict({
  action: t.literal('gitlab_repo_added'),
  changed: t.strict({
    newRepoName: t.string,
  }),
})

export const TGitLabRepoDeletedAction = t.strict({
  action: t.literal('gitlab_repo_removed'),
  changed: t.strict({
    oldRepoName: t.string,
  }),
})

export const TSubevent = t.intersection([
  t.strict({
    actionId: t.string,
  }),
  t.union([
    TTermCreatedAction,
    TTranslationAddedAction,
    TTranslationUpdatedAction,
  ]),
])

export const TImportFromFileAction = t.strict({
  action: t.literal('import_from_file'),
  changed: t.null,
})

export const TImportFromRepoAction = t.strict({
  action: t.literal('import_from_repo'),
  changed: t.null,
})

export const TImportFromGitlabAction = t.strict({
  action: t.literal('import_from_gitlab'),
  changed: t.null,
})

export const TFigmaFileAddedAction = t.strict({
  action: t.literal('figma_file_added'),
  changed: t.strict({
    newValue: t.string,
  }),
})

export const TFigmaFileDeletedAction = t.strict({
  action: t.literal('figma_file_removed'),
  changed: t.strict({
    oldValue: t.string,
  }),
})

export const TImportedFromFigmaAction = t.strict({
  action: t.literal('import_from_figma'),
  changed: t.null,
})

export type Subevent = t.TypeOf<typeof TSubevent>

export const TProjectHistoryItem = t.intersection([
  t.strict({
    actionId: t.string,
    createdAt: t.string,
    entity: t.string,
    user: TUser,
  }),
  t.union([
    TProjectCreatedAction,
    TProjectRemovedAction,
    TProjectAvatarUpdatedAction,
    TProjectNameUpdatedAction,
    TProjectDescriptionUpdatedAction,
    TProjectLanguagesUpdatedAction,

    TCollaboratorAddedAction,
    TCollabortorRemovedAction,
    TCollaboratorStatusChangedAction,

    TTermCreatedAction,
    TTermDeletedAction,
    TTermKeyUpdatedAction,
    TTermDescriptionUpdated,
    TTermTagsUpdatedAction,

    TTranslationAddedAction,
    TTranslationUpdatedAction,
    TTranslationDeletedAction,

    TGhRepoAddedAction,
    TGhRepoDeletedAction,

    TProjectLinkedAction,

    TImportFromFileAction,
    TImportFromRepoAction,

    TFigmaFileAddedAction,
    TFigmaFileDeletedAction,
    TImportedFromFigmaAction,

    TGitLabRepoAddedAction,
    TGitLabRepoDeletedAction,
    TImportFromGitlabAction,

    TProjectMainLangUpdatedAction,
  ]),
])

export type ProjectHistoryItem = t.TypeOf<typeof TProjectHistoryItem>
